// Premium Bottom Sheet component with Framer Motion animations
// REFACTORED: Simplified with unified behavior for all bottom sheets
// UPDATED: Reduced spacing between drag handle and title for better visual balance
// Features:
// - Spring physics animations (no cubic-bezier) with realistic bounce
// - Advanced swipe-to-dismiss with velocity-based exit animation
// - Drag handle with reactive visual feedback (scale + color change)
// - Progressive backdrop blur linked to drag position
// - Velocity-differentiated closing (slow/medium/fast spring presets)
// - Auto-disable swipe when content is scrolled
// - Synchronized with BottomSheetContext for background scale effects
// - React Portal for z-index isolation
// - Supports reduced motion preferences

import { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getPortalRoot } from '../../lib/portalUtils';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  PanInfo,
  useReducedMotion,
  useVelocity,
  useSpring,
} from 'framer-motion';
import { Z_BOTTOM_SHEET_BACKDROP, Z_BOTTOM_SHEET } from '../../lib/zIndex';
import { useBottomSheetOptional } from '../../contexts/BottomSheetContext';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxHeight?: string;
  showDragHandle?: boolean;
  enableSwipe?: boolean;
}

// Spring presets for different animation contexts
const springPresets = {
  default: { stiffness: 350, damping: 32, mass: 0.8 },
  snap: { stiffness: 400, damping: 35, mass: 0.7 },
  exit: { stiffness: 300, damping: 28, mass: 0.6 },
  exitFast: { stiffness: 450, damping: 30, mass: 0.5 },
};

// Velocity thresholds for differentiated closing behavior
const VELOCITY_LOW = 300; // px/s
const VELOCITY_MEDIUM = 800; // px/s
const CLOSE_DISTANCE_THRESHOLD = 100; // px
const RUBBER_BAND_RESISTANCE = 3;

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  maxHeight = '65vh',
  showDragHandle = true,
  enableSwipe = true,
}: BottomSheetProps) {
  const sheetId = useId();
  const [canScroll, setCanScroll] = useState(false);
  const [isDragHandleActive, setIsDragHandleActive] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  // Bottom sheet context for synchronization
  const bottomSheetContext = useBottomSheetOptional();

  // Motion values for drag
  const dragY = useMotionValue(0);
  const dragVelocity = useVelocity(dragY);

  // Spring for smooth animations
  const dragYSpring = useSpring(dragY, springPresets.default);

  // Transform backdrop opacity based on drag position
  const backdropOpacity = useTransform(dragY, [0, 200], [0.5, 0]);

  // Transform backdrop blur based on drag position
  const backdropBlur = useTransform(dragY, [0, 200], [12, 0]);

  // Transform backdrop filter value (created once, not in JSX)
  const backdropFilterValue = useTransform(backdropBlur, (blur) => `blur(${blur}px)`);

  // Transform drag handle scale and color based on drag
  const handleScale = useTransform(dragY, [0, 50], [1, 1.15]);

  // Check if content is scrolled
  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl || !enableSwipe) return;

    const handleScroll = () => {
      const isScrolled = contentEl.scrollTop > 0;
      setCanScroll(isScrolled);
    };

    contentEl.addEventListener('scroll', handleScroll);
    return () => contentEl.removeEventListener('scroll', handleScroll);
  }, [enableSwipe]);

  // Register/unregister with context and prevent body scroll
  useEffect(() => {
    if (isOpen) {
      bottomSheetContext?.registerSheet(sheetId);
      document.body.style.overflow = 'hidden';

      // Animate progress to 1
      setTimeout(() => {
        bottomSheetContext?.setProgress(1);
      }, 50);
    } else {
      document.body.style.overflow = '';
      bottomSheetContext?.setProgress(0);
    }

    return () => {
      document.body.style.overflow = '';
      bottomSheetContext?.unregisterSheet(sheetId);
      bottomSheetContext?.setProgress(0);
    };
  }, [isOpen, sheetId]); // Removed bottomSheetContext from dependencies

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleDragStart = () => {
    if (!enableSwipe || canScroll) return;
    setIsDragHandleActive(true);
  };

  const handleDrag = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (!enableSwipe || canScroll) return;

    const { offset } = info;

    if (offset.y > 0) {
      // Dragging down - allow with full range
      dragY.set(offset.y);

      // Update context progress for background scale effect
      const progress = Math.max(0, 1 - offset.y / 300);
      bottomSheetContext?.setProgress(progress);
    } else {
      // Dragging up - rubber band effect
      const resistance = Math.abs(offset.y) / RUBBER_BAND_RESISTANCE;
      dragY.set(-resistance);
    }
  };

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (!enableSwipe || canScroll) return;

    setIsDragHandleActive(false);

    const { offset } = info;
    const velocity = dragVelocity.get();

    // Determine spring preset based on velocity
    let shouldClose = false;
    let exitSpring = springPresets.exit;

    if (Math.abs(velocity) > VELOCITY_MEDIUM) {
      // High velocity - fast close
      shouldClose = offset.y > 50;
      exitSpring = springPresets.exitFast;
    } else if (Math.abs(velocity) > VELOCITY_LOW) {
      // Medium velocity - standard close
      shouldClose = offset.y > 75;
      exitSpring = springPresets.exit;
    } else {
      // Low velocity - distance-based
      shouldClose = offset.y > CLOSE_DISTANCE_THRESHOLD;
    }

    if (shouldClose) {
      onClose();
    } else {
      // Snap back to position
      dragY.set(0);
      bottomSheetContext?.setProgress(1);
    }
  };

  if (!isOpen) return null;

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const sheetVariants = {
    hidden: {
      y: '100%',
      transition: shouldReduceMotion
        ? { duration: 0.2 }
        : springPresets.exit,
    },
    visible: {
      y: 0,
      transition: shouldReduceMotion
        ? { duration: 0.3 }
        : springPresets.default,
    },
  };

  // Render using Portal to avoid stacking context issues
  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 flex items-end" style={{ zIndex: Z_BOTTOM_SHEET }}>
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            style={{
              zIndex: Z_BOTTOM_SHEET_BACKDROP,
              backdropFilter: backdropFilterValue,
              opacity: backdropOpacity,
            }}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Bottom Sheet */}
          <motion.div
            ref={sheetRef}
            className="relative w-full bg-white rounded-t-2xl shadow-2xl"
            style={{
              maxHeight,
              y: enableSwipe ? dragYSpring : 0,
              zIndex: Z_BOTTOM_SHEET,
              willChange: 'transform',
            }}
            variants={sheetVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            drag={enableSwipe ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.3 }}
            onDragStart={handleDragStart}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            dragMomentum={false}
          >
            {/* Drag handle */}
            {showDragHandle && (
              <motion.div
                className="flex justify-center py-2 cursor-grab active:cursor-grabbing"
                style={{ minHeight: '36px' }}
                animate={{
                  backgroundColor: isDragHandleActive ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0)',
                }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="w-12 h-1.5 bg-gray-300 rounded-full"
                  style={{ scale: handleScale }}
                  animate={{
                    backgroundColor: isDragHandleActive ? 'rgb(156, 163, 175)' : 'rgb(209, 213, 219)',
                  }}
                  transition={{ duration: 0.2 }}
                />
              </motion.div>
            )}

            {/* Header */}
            {title && (
              <div
                className="flex items-center justify-between px-6 py-4 border-b border-gray-100"
                style={{ minHeight: '64px' }}
              >
                <h3 className="text-lg font-semibold text-gray-900 flex-1">
                  {title}
                </h3>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                  aria-label="Fermer"
                >
                  <X size={24} />
                </button>
              </div>
            )}

            {/* Content with scroll and safe-area padding for iOS */}
            <div
              ref={contentRef}
              className="overflow-y-auto pb-[env(safe-area-inset-bottom)]"
              style={{
                maxHeight: title
                  ? `calc(${maxHeight} - ${showDragHandle ? '148px' : '112px'})`
                  : `calc(${maxHeight} - ${showDragHandle ? '36px' : '0px'})`,
              }}
            >
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    getPortalRoot()
  );
}
